import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnInit, Output} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {FroalaToolbarPreviewService} from '../../services/froala-toolbar-preview.service';

declare const window: any;

/**
 * Renders wysiwyg content as static html until the user interacts with the field.
 * Only then the actual froala editor gets initialized. Froala initialization is
 * expensive and large questionnaires create several hundred editor instances,
 * which made the editor slow to load and use (MYM-1029).
 */
@Component({
  selector: 'app-qa-lazy-froala',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './lazy-froala.component.html',
  styleUrls: ['./lazy-froala.component.css']
})
export class LazyFroalaComponent implements OnInit {
  /**
   * Froala editor options. Must be referentially stable, as usual for the froala directive
   */
  @Input() options: any;

  /**
   * Wysiwyg content (html)
   */
  @Input() model: string;

  /**
   * Renders the static preview as block editor field (e.g. for descriptions)
   * instead of an inline field (titles, answers)
   */
  @Input() block = false;

  /**
   * Emits when the user changed the content
   */
  @Output() modelChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Emits when the editor element loses focus. Mirrors the (blur) binding
   * that was previously placed directly on the froala element
   */
  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Is the real froala editor active (initialization started)?
   */
  public active = false;

  /**
   * Is the real froala editor fully initialized? The static preview stays
   * visible until then, so the swap happens without flickering
   */
  public ready = false;

  /**
   * Copy of the given options with a wrapped "initialized" event, so we can
   * focus the editor right after a user triggered activation
   */
  public wrappedOptions: any;

  /**
   * Toolbar html of a real froala editor for the static block preview.
   * Snapshot of the original toolbar, so the preview looks pixel perfect
   */
  public toolbarPreviewHtml: SafeHtml = null;

  /**
   * Should the editor get focused once it is initialized?
   */
  private pendingFocus = false;

  /**
   * Character offset where the cursor should be placed after initialization.
   * Captured from the click position in the static preview. -1 means "end of content"
   */
  private pendingCaretOffset = -1;

  /**
   * Injects dependencies
   */
  public constructor(
    private ref: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private toolbarPreview: FroalaToolbarPreviewService
  ) {
  }

  /**
   * Builds the wrapped froala options once and loads the
   * toolbar preview for block editor fields
   */
  public ngOnInit() {
    this.wrappedOptions = this.buildWrappedOptions();
    this.block && this.loadToolbarPreview();
  }

  /**
   * Activates the froala editor. Optionally focuses it after initialization
   * and places the cursor at the clicked position (if a mouse event is given)
   * or at the end of the content.
   *
   * @param {boolean} focus
   * @param {MouseEvent} event Optional mouse event to derive the cursor position from
   */
  public activate(focus: boolean = true, event?: MouseEvent) {
    if (this.active) {
      return;
    }

    this.pendingFocus = focus;
    this.pendingCaretOffset = event ? this.getCaretOffsetFromEvent(event) : -1;
    this.active = true;
    this.ref.markForCheck();
  }

  /**
   * Event handler for content changes of the active editor
   *
   * @param {string} model
   */
  public onModelChange(model: string) {
    this.model = model;
    this.modelChange.emit(model);
  }

  /**
   * Loads the snapshotted froala toolbar html for the static block preview.
   * The html is generated by a real froala editor, so the sanitizer bypass
   * only ever renders markup that froala itself would render
   */
  private loadToolbarPreview() {
    this.toolbarPreview.getToolbarHtml(this.options).then((html) => {
      if (!html || this.ready) {
        return;
      }

      this.toolbarPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(html);
      this.ref.markForCheck();
    });
  }

  /**
   * Returns a copy of the given options where the "initialized" event first calls
   * the original handler (if any) and then applies the pending focus. The copy is
   * built once and stays referentially stable, so the froala directive does not
   * deep clone it on every change detection cycle.
   */
  private buildWrappedOptions(): any {
    const self = this;
    const options = Object.assign({}, this.options);
    options.events = Object.assign({}, this.options && this.options.events);

    const originalInitialized = options.events['initialized'];

    options.events['initialized'] = function() {
      originalInitialized && originalInitialized.call(this);
      self.onEditorInitialized(this);
    };

    return options;
  }

  /**
   * Swaps the static preview for the fully initialized editor and applies
   * a pending focus. Runs inside the angular zone, as froala events may
   * fire outside of it
   *
   * @param {any} editor Froala editor instance
   */
  private onEditorInitialized(editor: any) {
    this.zone.run(() => {
      this.ready = true;
      this.ref.markForCheck();
      this.pendingFocus && this.focusEditor(editor, this.pendingCaretOffset);
      this.pendingFocus = false;
      this.pendingCaretOffset = -1;
    });
  }

  /**
   * Returns the character offset of the click position within the static
   * preview content, so the cursor can be restored at the same position in
   * the real editor. Returns -1 (end of content) if it cannot be determined
   *
   * @param {MouseEvent} event
   * @returns {number}
   */
  private getCaretOffsetFromEvent(event: MouseEvent): number {
    const container = <HTMLElement>event.currentTarget;
    const root = container && container.querySelector('.qa-lazy-froala');

    if (!root) {
      return -1;
    }

    const doc = <any>document;
    let node: Node = null;
    let offset = 0;

    // chrome, edge and safari
    if (doc.caretRangeFromPoint) {
      const range = doc.caretRangeFromPoint(event.clientX, event.clientY);
      range && (node = range.startContainer) && (offset = range.startOffset);
    // firefox
    } else if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(event.clientX, event.clientY);
      pos && (node = pos.offsetNode) && (offset = pos.offset);
    }

    // clicks outside of the content (e.g. on the toolbar preview) focus the end
    if (!node || !root.contains(node)) {
      return -1;
    }

    // measure the text length from content start to the caret position
    const measure = document.createRange();
    measure.selectNodeContents(root);
    measure.setEnd(node, offset);
    return measure.toString().length;
  }

  /**
   * Places the cursor at given character offset within the editor element.
   * Returns false if the offset could not be resolved
   *
   * @param {HTMLElement} rootEl Editor content element
   * @param {number} chars Character offset
   * @returns {boolean}
   */
  private setCaretAtTextOffset(rootEl: HTMLElement, chars: number): boolean {
    const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, null);
    let node: Node = walker.nextNode();

    while (node) {
      const length = node.textContent.length;

      if (chars <= length) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.setStart(node, chars);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return true;
      }

      chars -= length;
      node = walker.nextNode();
    }

    return false;
  }

  /**
   * Focuses given froala editor instance and places the cursor at given
   * character offset, or at the end of the content if the offset is -1
   * or cannot be resolved.
   *
   * @param {any} editor Froala editor instance
   * @param {number} caretOffset Character offset for the cursor position
   */
  private focusEditor(editor: any, caretOffset: number = -1) {
    setTimeout(() => {
      if (!editor || !editor.events || !editor.el) {
        return;
      }

      editor.events.focus();

      if (caretOffset >= 0 && this.setCaretAtTextOffset(editor.el, caretOffset)) {
        return;
      }

      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editor.el);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }, 0);
  }
}
